import { motion } from "framer-motion";
import { Instagram, MessageCircle, MapPin, Phone, Star, Navigation } from "lucide-react";
import { EVENT } from "../../data/content";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../i18n/LanguageContext";

export const FutureVision = () => {
  const { t } = useLang();
  const s = t.footer;
  const waLink = EVENT.whatsapp;

  return (
    <footer id="contact" data-testid="future-vision-section" className="relative overflow-hidden bg-ink-900">
      <section className="relative overflow-hidden py-28 md:py-40">
        <img
          src="https://images.unsplash.com/photo-1614080035039-af24354a6478?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-900/70 to-ink-900" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold-primary/15 blur-[130px]" />

        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <p className="mb-5 text-xs uppercase tracking-[0.3em] text-gold-light md:text-sm">{s.legacyEyebrow}</p>
          <h2 className="font-display text-5xl font-light leading-[1.02] tracking-tight text-white md:text-7xl">
            {s.legacyTitle} <span className="text-gold-gradient italic">{s.legacyAccent}</span>
          </h2>
          <p className="mx-auto mt-7 max-w-2xl text-base font-light leading-relaxed text-white/70 md:text-lg">{s.legacyBody}</p>
          <p className="mx-auto mt-5 max-w-xl text-sm text-white/50">{s.legacyNote}</p>
          <button
            onClick={() => scrollToId("#register")}
            data-testid="legacy-register-btn"
            className="mt-9 inline-block rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-9 py-4 text-sm font-medium text-ink-900 transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(217,119,6,0.6)]"
          >
            {s.legacyCta}
          </button>
        </motion.div>
      </section>

      <section className="border-t border-white/10 py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-8 lg:grid-cols-3">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="glass rounded-2xl p-7">
            <Navigation className="mb-4 h-7 w-7 text-gold-light" />
            <h3 className="font-display text-2xl text-white">{s.venueTitle}</h3>
            <p className="mt-2 text-sm font-medium text-gold-light">{s.venueName}</p>
            <p className="mt-3 text-sm leading-relaxed text-white/65">{s.venueBody}</p>
            <a
              href={EVENT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-maps"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold-light underline-offset-4 hover:underline"
            >
              <MapPin className="h-4 w-4" /> {s.mapsCta}
            </a>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="glass rounded-2xl p-7">
            <MapPin className="mb-4 h-7 w-7 text-gold-light" />
            <h3 className="font-display text-2xl text-white">{s.homeTitle}</h3>
            <p className="mt-2 text-sm font-medium text-gold-light">{s.homeName}</p>
            <p className="mt-3 text-sm leading-relaxed text-white/65">{s.homeBody}</p>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="glass rounded-2xl p-7">
            <Star className="mb-4 h-7 w-7 text-gold-light" fill="currentColor" />
            <h3 className="font-display text-2xl text-white">{s.contactTitle}</h3>
            <div className="mt-4 space-y-3">
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-gold-light">
                <MessageCircle className="h-5 w-5 text-[#25D366]" /> {s.whatsapp} · {EVENT.phoneDisplay}
              </a>
              <a href={`tel:+91${EVENT.phone}`} data-testid="footer-call" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-gold-light">
                <Phone className="h-5 w-5 text-gold-light" /> {s.call} · {EVENT.phoneDisplay}
              </a>
              <a href={`https://instagram.com/${EVENT.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-gold-light">
                <Instagram className="h-5 w-5 text-gold-light" /> @{EVENT.instagram}
              </a>
            </div>
          </motion.div>
        </div>

        <div className="mx-auto mt-16 max-w-7xl px-6 md:px-8">
          <div className="divider-gold" />
          <div className="flex flex-col items-center justify-between gap-4 pt-8 md:flex-row">
            <p className="font-display text-2xl text-white">Sapiens <span className="text-gold-gradient">Celestia</span></p>
            <div className="text-center md:text-right">
              <p className="text-xs text-white/40">{EVENT.hashtag} · {s.copyright}</p>
              <p className="mt-1 text-xs tracking-wide text-gold-light/70" data-testid="site-credit">{s.credit}</p>
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
};
